import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, Loader2, RefreshCw, Link2, Unlink, Zap, Info } from "lucide-react";

const AIRBNB_HELP = "Airbnb → your listing → Availability → Sync calendars → Export calendar. Copy that link and paste it here.";

// Suggestions only: imported checkout days are shown to schedule. Nothing is booked or charged automatically.
export default function CalendarPanel({ property, favourites, onChanged }) {
  const navigate = useNavigate();
  const [url, setUrl] = useState(property.ical_url || "");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const connected = !!(view?.ical_url ?? property.ical_url);
  const turnovers = view?.turnovers ?? null;

  const call = async (fn) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setView(data);
      onChanged?.();
      return data;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const connect = async () => {
    if (!url.trim()) { toast.error("Paste your Airbnb calendar link first."); return; }
    const data = await call(() => api.post(`/properties/${property.property_id}/calendar`, { ical_url: url.trim() }));
    if (data) toast.success(`Calendar connected — ${data.turnovers.length} upcoming turnover${data.turnovers.length === 1 ? "" : "s"} found`);
  };

  const sync = async () => {
    const data = await call(() => api.post(`/properties/${property.property_id}/calendar/sync`));
    if (data) toast.success("Calendar synced");
  };

  const load = () => call(() => api.get(`/properties/calendar/turnovers`).then((r) => ({
    data: r.data.properties.find((p) => p.property_id === property.property_id) || { turnovers: [] },
  })));

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.delete(`/properties/${property.property_id}/calendar`);
      setView({ ical_url: null, turnovers: [] });
      setUrl("");
      toast.success("Calendar disconnected");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const schedule = (t) => {
    const cleaner = favourites?.[0]?.cleaner_id;
    const qs = `property=${property.property_id}&date=${t.date}&slot=morning`;
    navigate(cleaner ? `/book/${cleaner}?${qs}` : `/search?${qs}`);
  };

  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid={`calendar-panel-${property.property_id}`}>
      <div className="flex items-center gap-2">
        <CalendarDays className="h-4 w-4 text-accent" />
        <h4 className="text-sm font-semibold">Airbnb calendar</h4>
        {connected && (
          <span className="ml-auto text-xs text-muted-foreground">
            {view?.ical_last_synced || property.ical_last_synced
              ? `Synced ${new Date(view?.ical_last_synced || property.ical_last_synced).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}`
              : ""}
          </span>
        )}
      </div>

      {!connected ? (
        <div className="mt-2">
          <p className="text-sm text-muted-foreground">{AIRBNB_HELP}</p>
          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.airbnb.co.uk/calendar/ical/…" className="flex-1 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent text-sm" data-testid={`ical-input-${property.property_id}`} />
            <button onClick={connect} disabled={busy} className="px-5 py-2.5 rounded-full bg-accent text-white text-sm font-medium hover:bg-accent/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid={`ical-connect-${property.property_id}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Connect
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2">
          <div className="flex flex-wrap gap-2">
            <button onClick={sync} disabled={busy} className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-secondary transition-colors inline-flex items-center gap-1.5 disabled:opacity-50" data-testid={`ical-sync-${property.property_id}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sync now
            </button>
            {turnovers === null && (
              <button onClick={load} disabled={busy} className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-secondary transition-colors" data-testid={`ical-show-${property.property_id}`}>
                Show turnovers
              </button>
            )}
            <button onClick={disconnect} disabled={busy} className="px-4 py-2 rounded-full border border-border text-sm font-medium text-muted-foreground hover:text-destructive transition-colors inline-flex items-center gap-1.5" data-testid={`ical-disconnect-${property.property_id}`}>
              <Unlink className="h-3.5 w-3.5" /> Disconnect
            </button>
          </div>

          {view?.ical_error && <p className="text-sm text-destructive mt-2">{view.ical_error}</p>}

          {turnovers !== null && (
            turnovers.length === 0 ? (
              <p className="text-sm text-muted-foreground mt-3" data-testid={`no-turnovers-${property.property_id}`}>No upcoming checkout days in this calendar.</p>
            ) : (
              <div className="mt-3 space-y-2" data-testid={`turnover-list-${property.property_id}`}>
                {turnovers.map((t) => (
                  <div key={t.date} className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-secondary/50 border border-border">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {new Date(t.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                      </p>
                      <p className="text-xs text-muted-foreground">Guest checks out{t.guest_nights ? ` after ${t.guest_nights} night${t.guest_nights === 1 ? "" : "s"}` : ""}</p>
                    </div>
                    {t.same_day_turnaround && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium" data-testid={`same-day-${t.date}`}>
                        <Zap className="h-3 w-3" /> Same-day turnaround
                      </span>
                    )}
                    {t.scheduled ? (
                      <span className="ml-auto text-xs font-medium text-emerald-600" data-testid={`turnover-scheduled-${t.date}`}>Clean booked</span>
                    ) : (
                      <button onClick={() => schedule(t)} className="ml-auto px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors" data-testid={`schedule-turnover-${t.date}`}>
                        Schedule clean
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )
          )}

          <p className="text-xs text-muted-foreground mt-3 flex items-start gap-1.5">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            Suggestions only — we never book a cleaner or take payment automatically. You confirm every clean.
          </p>
        </div>
      )}
    </div>
  );
}
